import React, { useEffect, useState } from 'react';
import {
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Spinner,
} from 'reactstrap';
import { toast } from 'react-toastify';
import SideModal from '../../components/Common/SideModal';
import { workTypesAPI } from '../../helpers/api';

interface WorkType {
  id: string;
  name: string;
  unit: string;
  description?: string;
  isActive: boolean;
}

interface WorkTypeFormModalProps {
  isOpen: boolean;
  toggle: () => void;
  onSuccess: () => void;
  editingWorkType?: WorkType | null;
}

// Default unit options (fallback)
const DEFAULT_UNITS = ['m²', 'm³', 'ton', 'kg', 'metre', 'adet', 'litre', 'saat'];

const WorkTypeFormModal: React.FC<WorkTypeFormModalProps> = ({
  isOpen,
  toggle,
  onSuccess,
  editingWorkType,
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    unit: '',
    customUnit: '',
    description: '',
  });

  // Units for dropdown
  const [units, setUnits] = useState<string[]>([]);

  useEffect(() => {
    if (isOpen) {
      fetchUnits();
      if (editingWorkType) {
        // Will be updated after units are fetched
        setFormData({
          name: editingWorkType.name,
          unit: editingWorkType.unit,
          customUnit: '',
          description: editingWorkType.description || '',
        });
      } else {
        resetForm();
      }
    }
  }, [isOpen, editingWorkType]);

  // Update form when units are loaded (to handle custom values)
  useEffect(() => {
    if (editingWorkType && units.length > 0) {
      const unitInList = units.includes(editingWorkType.unit);
      setFormData(prev => ({
        ...prev,
        unit: unitInList ? editingWorkType.unit : '__OTHER__',
        customUnit: unitInList ? '' : editingWorkType.unit,
      }));
    }
  }, [editingWorkType, units]);

  const resetForm = () => {
    setFormData({
      name: '',
      unit: '',
      customUnit: '',
      description: '',
    });
  };

  const fetchUnits = async () => {
    try {
      const response = await workTypesAPI.getUnits();
      const dbUnits = response.data as string[];
      // Merge DB units with defaults, removing duplicates
      const allUnits = [...new Set([...dbUnits, ...DEFAULT_UNITS])].sort();
      setUnits(allUnits);
    } catch (error) {
      console.error('Units fetch error:', error);
      setUnits(DEFAULT_UNITS);
    }
  };

  // Get actual unit value (from dropdown or custom input)
  const getActualUnit = () => formData.unit === '__OTHER__' ? formData.customUnit : formData.unit;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const actualUnit = getActualUnit();

    if (!formData.name.trim() || !actualUnit.trim()) {
      toast.error('İş adı ve birim zorunludur');
      return;
    }

    try {
      setSubmitting(true);

      const payload = {
        name: formData.name.trim(),
        unit: actualUnit.trim(),
        description: formData.description.trim() || undefined,
      };

      if (editingWorkType) {
        await workTypesAPI.update(editingWorkType.id, payload);
        toast.success('İş türü güncellendi');
      } else {
        await workTypesAPI.create(payload);
        toast.success('İş türü oluşturuldu');
      }

      onSuccess();
      toggle();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'İşlem başarısız');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <SideModal
      isOpen={isOpen}
      toggle={toggle}
      title={editingWorkType ? 'İş Türü Düzenle' : 'Yeni İş Türü'}
      size="md"
    >
      <div style={{ margin: '-1.25rem', padding: '1rem', background: '#f3f3f9', minHeight: '100%' }}>
        <Form onSubmit={handleSubmit} className="worktype-form">
          {/* İş Bilgileri Card */}
          <div style={{ background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderTop: '3px solid #f7b84b' }} className="rounded-3 p-3 mb-3">
            <h6 className="mb-3" style={{ fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.7rem' }}>
              <i className="ri-file-list-3-line me-2"></i>
              İş Bilgileri
            </h6>
            <div>
            <FormGroup>
              <Label>İş Adı *</Label>
              <Input
                type="text"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                placeholder="Örn: Beton Dökümü"
                required
              />
            </FormGroup>

            <FormGroup>
              <Label>Birim *</Label>
              <Input
                type="select"
                value={formData.unit}
                onChange={(e) => {
                  const value = e.target.value;
                  setFormData({
                    ...formData,
                    unit: value,
                    customUnit: value === '__OTHER__' ? formData.customUnit : '',
                  });
                }}
              >
                <option value="">Birim Seçin</option>
                {units.map((unit) => (
                  <option key={unit} value={unit}>{unit}</option>
                ))}
                <option value="__OTHER__">+ Diğer (Yeni Birim)</option>
              </Input>
              {formData.unit === '__OTHER__' && (
                <Input
                  type="text"
                  value={formData.customUnit}
                  onChange={(e) => setFormData({ ...formData, customUnit: e.target.value })}
                  placeholder="Yeni birim girin"
                  className="mt-2"
                />
              )}
              {/* Quick select buttons */}
              <div className="mt-2">
                {DEFAULT_UNITS.slice(0, 6).map((unit) => (
                  <Button
                    key={unit}
                    color={getActualUnit() === unit ? 'primary' : 'soft-secondary'}
                    size="sm"
                    className="me-1 mb-1"
                    type="button"
                    onClick={() => setFormData({ ...formData, unit, customUnit: '' })}
                  >
                    {unit}
                  </Button>
                ))}
              </div>
            </FormGroup>

            <FormGroup className="mb-0">
              <Label>Açıklama</Label>
              <Input
                type="textarea"
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                placeholder="İş türü hakkında kısa açıklama..."
                rows={2}
              />
            </FormGroup>
            </div>
          </div>

          {/* Kaydet Butonu */}
          <div className="form-actions">
            <Button color="secondary" onClick={toggle} disabled={submitting}>
              İptal
            </Button>
            <Button color="primary" type="submit" disabled={submitting}>
              {submitting ? (
                <>
                  <Spinner size="sm" className="me-1" />
                  Kaydediliyor...
                </>
              ) : (
                <>
                  <i className="ri-save-line me-1"></i>
                  {editingWorkType ? 'Güncelle' : 'Oluştur'}
                </>
              )}
            </Button>
          </div>
        </Form>
      </div>
    </SideModal>
  );
};

export default WorkTypeFormModal;
